/**
 * atb-shipping-policy.js
 * --------------------------------------------------------------------------
 * Scroll-reveal hydration for the editorial Shipping & Delivery page.
 *
 * Adds `.is-revealed` to each `[data-atb-reveal]` element as it scrolls into
 * view, which the matching CSS uses to:
 *   - fade-up + slide-in each section
 *   - draw the 4-step timeline line cascade left-to-right
 *   - light up the timeline dots in sequence
 *
 * IntersectionObserver-based, single-shot per element. No dependencies.
 *
 * Loaded via `<script src="...atb-shipping-policy.js" defer>` from the
 * section file — runs after parse + DOM but before paint of below-the-fold
 * content typically lands in viewport, so reveals feel natural rather than
 * "popping in late."
 *
 * Reduced-motion is handled by CSS, not here. The CSS resets all the
 * transitions to instant under prefers-reduced-motion, so the JS can keep
 * adding the class without causing motion.
 */

(function () {
  'use strict';

  const reveals = document.querySelectorAll('[data-atb-reveal]');
  if (!reveals.length) return;

  // Fallback for ancient browsers — just reveal everything immediately.
  if (!('IntersectionObserver' in window)) {
    reveals.forEach(function (el) { el.classList.add('is-revealed'); });
    return;
  }

  const observer = new IntersectionObserver(
    function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        // Single-shot — once revealed, stop observing this element.
        obs.unobserve(entry.target);
      });
    },
    {
      // Trigger LATER in the scroll than before — the user complained
      // animations were completing before they could see them. Previous
      // `rootMargin: 0 0 -15% 0` fired when the top edge entered the
      // bottom 85% of the viewport, which on tall sections meant the
      // animation finished while the interesting content was still
      // below the fold. Now requires `-25%` (top of element in bottom
      // 75% of viewport) AND 15% of the element visible. Net effect:
      // the user has to actually scroll the element well into view
      // before the animation kicks off.
      rootMargin: '0px 0px -25% 0px',
      threshold: 0.15,
    }
  );

  reveals.forEach(function (el) {
    observer.observe(el);
  });

  // Defensive: also reveal the footer CTA. It's not in [data-atb-reveal] but
  // the CSS waits for .is-revealed. (We could also just add data-atb-reveal
  // to it in the section file — done there too. Belt-and-suspenders here.)
  const footerCta = document.querySelector('.atb-shipping__footer-cta:not([data-atb-reveal])');
  if (footerCta) {
    const footerObs = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -15% 0px', threshold: 0.05 }
    );
    footerObs.observe(footerCta);
  }
})();
